import type { Browser, Device, DeviceInfoCustomFlags, DeviceInfoInterface, Platform } from './deviceInfoTypes';
import { DeviceInfoDefaults } from './deviceInfoTypes';
import { generateCustomFlags } from './utils/generateCustomFlags';
import { getBrowser } from './utils/getBrowser';
import { getDevice } from './utils/getDevice';
import { getIsMobile } from './utils/getIsMobile';
import { getIsTouch } from './utils/getIsTouch';
import { getPlatform } from './utils/getPlatform';

export enum DeviceType {
  Mobile = 'mobile',
  Desktop = 'desktop',
}

/**
 * DeviceInfo is a singleton that provides information about the device, browser and feature support.
 */
class DeviceInfoClass {
  private initialized: boolean = false;
  private deviceInfo: DeviceInfoInterface = DeviceInfoDefaults;

  init(userAgent: string = navigator.userAgent || navigator.vendor): void {
    // Stage 1 -  Parse User Agent and generate flags.
    this.deviceInfo.platform = getPlatform(userAgent);
    this.deviceInfo.device = getDevice(userAgent);
    this.deviceInfo.browser = getBrowser(userAgent);
    this.deviceInfo.isMobile = getIsMobile();
    this.deviceInfo.isTouch = getIsTouch();

    // Stage 2 - Depends of Stage 1
    // TODO: Add feature checks (touch, multITouchObg, webgl, webaudio etc.)
    // TODO: Generate deviceInfo flags. E.g. isMobile, isTablet, isDesktop etc.
    this.deviceInfo.customFlags = generateCustomFlags(this.deviceInfo);

    this.initialized = true;
  }

  isTouch(): boolean {
    this.initCheck();
    return this.deviceInfo.isTouch;
  }

  private initCheck(): boolean {
    if (!this.initialized) {
      console.error('DeviceInfo is not initialized. Please call DeviceInfo.init() first.');
    }

    return this.initialized;
  }

  isMobile(): boolean {
    this.initCheck();
    return this.deviceInfo.isMobile;
  }

  deviceType(): DeviceType {
    this.initCheck();
    const isMobile = this.deviceInfo.isMobile;
    return isMobile ? DeviceType.Mobile : DeviceType.Desktop;
  }

  getPlatform(): Platform {
    this.initCheck();
    return this.deviceInfo.platform;
  }

  getDevice(): Device {
    this.initCheck();
    return this.deviceInfo.device;
  }

  getBrowser(): Browser {
    this.initCheck();
    return this.deviceInfo.browser;
  }

  getCustomFlags(): DeviceInfoCustomFlags {
    this.initCheck();
    return this.deviceInfo.customFlags;
  }

  /**
   * Used in unit tests to reset the singleton instance.
   */
  dispose(): void {
    this.deviceInfo = {} as DeviceInfoInterface;
    this.initialized = false;
  }
}

export const DeviceInfo = new DeviceInfoClass();
